import "./ConvertServiceModal.scss";

import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import type { OptionType } from "components/inputs/Select/Select";
import Select from "components/inputs/Select/Select";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useConvertFMServiceMutation,
  usePatchMSATypeMutation,
} from "services/msa-api";
import { useConstants } from "utils/hooks";

type ConvertServiceModalProps = {
  serviceId: string;
  currentServiceType: string;
  isOpen: boolean;
  onClose: () => void;
};

// determine what service types can be converted to based on current type
const getConversionOptions = (
  currentTypeLabel: string,
  serviceTypes: Array<{ value: string | null; label: string }>,
  fungibleMSATypes: Array<{ value: string | null; label: string }>,
): Array<{ value: string; label: string }> => {
  // filter out null values and find the current type
  const allTypes = serviceTypes.filter((t) => t.value !== null);
  // try to find by label first, then by value (handles both cases)
  const currentType =
    allTypes.find((t) => t.label === currentTypeLabel) ||
    allTypes.find((t) => t.value === currentTypeLabel);

  if (!currentType || !currentType.value) return [];

  // define MSA type values and Cost Projection values
  const msaTypeValues = ["MSA", "MSA-111", "NTMSA", "PRE-MSA", "ARV"];
  const costProjectionValues = ["LCP", "MCP"];

  const options: Array<{ value: string; label: string }> = [];

  if (msaTypeValues.includes(currentType.value)) {
    // MSA types can convert to other MSA types or to Cost Projection types
    // add other MSA types (excluding current type)
    const fungibleTypes = fungibleMSATypes.filter(
      (t) => t.value !== null && t.value !== currentType.value,
    );
    fungibleTypes.forEach((type) => {
      if (type.value) {
        options.push({ value: type.value, label: type.label });
      }
    });

    // add Cost Projection type options (LCP and MCP)
    costProjectionValues.forEach((value) => {
      const type = allTypes.find((t) => t.value === value);
      if (type && type.value) {
        options.push({ value: type.value, label: type.label });
      }
    });
  } else if (costProjectionValues.includes(currentType.value)) {
    // Cost Projection types can convert to other Cost Projection type or to MSA types
    // add the other cost projection type
    costProjectionValues.forEach((value) => {
      if (value !== currentType.value) {
        const type = allTypes.find((t) => t.value === value);
        if (type && type.value) {
          options.push({ value: type.value, label: type.label });
        }
      }
    });

    // add all MSA type options
    msaTypeValues.forEach((value) => {
      const type = allTypes.find((t) => t.value === value);
      if (type && type.value) {
        options.push({ value: type.value, label: type.label });
      }
    });
  }

  return options;
};

const ConvertServiceModal = ({
  serviceId,
  currentServiceType,
  isOpen,
  onClose,
}: ConvertServiceModalProps) => {
  const { constants } = useConstants();

  const navigate = useNavigate();
  const [selectedServiceType, setSelectedServiceType] = useState<
    OptionType | undefined
  >(undefined);
  const [convertFMService, { isLoading: isConverting }] =
    useConvertFMServiceMutation();
  const [patchMSAType, { isLoading: isPatching }] = usePatchMSATypeMutation();

  const conversionOptions = getConversionOptions(
    currentServiceType,
    constants?.SERVICE_TYPE || [],
    constants?.FUNGIBLE_MSA_TYPES || [],
  );

  // determine if this is a fungible MSA type change (doesn't create new service)
  const isFungibleMSAChange = (targetValue: string): boolean => {
    const fungibleValues =
      constants?.FUNGIBLE_MSA_TYPES?.filter((t) => t.value !== null).map(
        (t) => t.value,
      ) || [];

    // find current type value from constants (handle both label and value)
    const currentTypeValue =
      constants?.SERVICE_TYPE?.find((t) => t.label === currentServiceType)
        ?.value ||
      constants?.SERVICE_TYPE?.find((t) => t.value === currentServiceType)
        ?.value;

    return (
      fungibleValues.includes(currentTypeValue) &&
      fungibleValues.includes(targetValue)
    );
  };

  const handleConvertService = async () => {
    if (!selectedServiceType || !serviceId) return;

    const targetValue = selectedServiceType.value as string;

    try {
      // for fungible MSA type changes, use the change-type endpoint (just updates type field)
      if (isFungibleMSAChange(targetValue)) {
        await patchMSAType({
          msaId: serviceId,
          data: { type: targetValue },
        }).unwrap();

        KaboodleToaster.success(
          `MSA type updated successfully to ${selectedServiceType.label}.`,
        );
        onClose();
        // refresh the page to show updated type
        window.location.reload();
      } else {
        // for actual conversions (creates new service), use the convert endpoint
        // send the specific service type constant value directly
        const response = await convertFMService({
          serviceId,
          data: { service_type: targetValue },
          serviceType: currentServiceType,
        }).unwrap();

        // redirect to the new service URL using the workspace_url from response
        if (response && (response as { workspace_url: string }).workspace_url) {
          navigate((response as { workspace_url: string }).workspace_url);

          KaboodleToaster.success(
            `Service converted successfully to ${selectedServiceType.label}.`,
          );
        } else {
          console.error("Invalid response structure:", response);
          KaboodleToaster.error("Conversion succeeded but navigation failed.");
        }
        onClose();
      }
    } catch (error: unknown) {
      console.error(error);
      const errorMessage =
        error &&
        typeof error === "object" &&
        "data" in error &&
        error.data &&
        typeof error.data === "object" &&
        "detail" in error.data
          ? String(error.data.detail)
          : "Failed to convert service. Please try again.";
      KaboodleToaster.error(errorMessage);
    }
  };

  const isLoading = isConverting || isPatching;

  // check if there are no conversion options available
  if (conversionOptions.length === 0) {
    return (
      <KaboodleModal isOpen={isOpen} onClose={onClose}>
        <Card heading="Convert Service Type">
          <p>
            This service type cannot be converted. Only MSA types can be
            converted to Cost Projections, and Cost Projections can be converted
            to MSAs.
          </p>
        </Card>
      </KaboodleModal>
    );
  }

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={onClose}
      footer={
        <div className="flex jc-c">
          <Button
            onClick={handleConvertService}
            disabled={!selectedServiceType || isLoading}
          >
            {isLoading ? "Converting..." : "Convert"}
          </Button>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
        </div>
      }
    >
      <Card heading="Convert Service Type">
        <div className="convert-service-current">
          <span className="material-icons">swap_horiz</span>
          <p>
            <strong>Current Type:</strong> {currentServiceType}
          </p>
        </div>

        <div className="convert-service-target">
          <label>Convert To:</label>
          <Select
            name="serviceType"
            placeholder="Select service type..."
            options={conversionOptions}
            value={selectedServiceType as OptionType}
            onChange={(e) => setSelectedServiceType(e as OptionType)}
            required
          />
        </div>

        {selectedServiceType &&
          !isFungibleMSAChange(selectedServiceType.value as string) && (
            <div className="convert-service-warning">
              <span className="material-icons">info</span>
              This will create a new service and delete the current one. All
              data will be transferred to the new service.
            </div>
          )}
      </Card>
    </KaboodleModal>
  );
};

export default ConvertServiceModal;
